import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import {
  AlignmentStats,
  FunnelCounts,
  TagStats,
} from 'features/timeSeries/experiment/types'
import { FC } from 'react'
import styled from 'styled-components'

const Strip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.forecast.background2};
  font-size: 0.78rem;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
`

const Flow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
`

const Step = styled.span`
  display: inline-flex;
  flex-direction: column;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  min-width: 5.5rem;
`

const StepLabel = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StepValue = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`

const Arrow = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Meta = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.72rem;
  line-height: 1.4;
`

const Callout = styled.div`
  padding: 0.55rem 0.7rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.pageBackground1};
  font-size: 0.74rem;
  line-height: 1.4;
`

const CalloutTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr));
  gap: 0.4rem;
  margin: 0.45rem 0;
`

const Stat = styled.div`
  padding: 0.35rem 0.45rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background2};
`

const StatLabel = styled.div`
  font-size: 0.62rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StatValue = styled.div`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 0.85rem;
`

const TagTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  th,
  td {
    text-align: left;
    padding: 0.2rem 0.35rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
  }
`

const Warn = styled.div`
  color: #f97316;
  font-size: 0.74rem;
  line-height: 1.35;
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

const fmtSpacing = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds))
    return '—'
  if (seconds < 90) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`
  return `${(seconds / 3600).toFixed(1)} h`
}

const samplingSummary = (funnel: FunnelCounts) => {
  if (funnel.sampling_mode === 'raw') {
    return t`Sampling: RAW historian points, outer-joined on exact timestamps (no mean binning).`
  }
  const iv = funnel.interval ?? funnel.interval_minutes
  const unit = funnel.interval_unit ?? 'minutes'
  return t`Sampling: aggregate ${{ value146_34: funnel.aggregation_op ?? 'mean' }} @ ${{ value146_71: iv ?? '?' }} ${unit} · gap_fill=${{ value146_103: funnel.gap_fill ?? 'off' }}`
}

export interface TransparencyStripProps {
  funnel: FunnelCounts | null
  alignment?: AlignmentStats | null
  tagStats?: Record<string, TagStats>
  loading?: boolean
  error?: string | null
  warnings?: string[]
  validationNote?: string | null
}

export const TransparencyStrip: FC<TransparencyStripProps> = ({
  funnel,
  alignment: alignmentProp,
  tagStats,
  loading,
  error,
  warnings,
  validationNote,
}) => {
  const { t } = useLingui()

  const split = funnel?.split_rows
  const alignment = alignmentProp ?? funnel?.alignment ?? null

  return (
    <Strip>
      <Title>
        <KnobHelp
          label={t`What data is actually used${{ value177_48: loading ? ' (updating…)' : '' }}`}
          help={KNOB_HELP.transparency}
        />
      </Title>
      {error && <Meta style={{ color: '#ef4444' }}>{error}</Meta>}
      {warnings?.map((w) => (
        <Warn key={w}>{w}</Warn>
      ))}
      {!funnel && !loading && (
        <Meta>
          <Trans>
            Pick a target and drivers to see how many points are used.
          </Trans>
        </Meta>
      )}
      {funnel && (
        <>
          <Meta>{samplingSummary(funnel)}</Meta>
          <Flow>
            <Step>
              <StepLabel>
                <Trans>Joined rows</Trans>
              </StepLabel>
              <StepValue>{funnel.raw_grid_rows ?? '—'}</StepValue>
            </Step>
            <Arrow>→</Arrow>
            <Step>
              <StepLabel>
                <Trans>After cleaning</Trans>
              </StepLabel>
              <StepValue>{funnel.after_cleaning_rows ?? '—'}</StepValue>
            </Step>
            <Arrow>→</Arrow>
            <Step>
              <StepLabel>
                <Trans>Target observed</Trans>
              </StepLabel>
              <StepValue>{funnel.target_observed_rows ?? '—'}</StepValue>
            </Step>
            <Arrow>→</Arrow>
            <Step>
              <StepLabel>
                <Trans>After lag warm-up</Trans>
              </StepLabel>
              <StepValue>{funnel.after_warmup_rows ?? '—'}</StepValue>
            </Step>
            <Arrow>→</Arrow>
            <Step>
              <StepLabel>
                <Trans>Train / val / test</Trans>
              </StepLabel>
              <StepValue>
                {split?.train ?? 0} / {split?.val ?? 0} / {split?.test ?? 0}
              </StepValue>
            </Step>
            <Arrow>→</Arrow>
            <Step>
              <StepLabel>
                <Trans>~Fit / band-calib</Trans>
              </StepLabel>
              <StepValue>
                {funnel.estimated_fit_rows ?? funnel.train_rows ?? '—'} /{' '}
                {funnel.estimated_calib_rows ?? funnel.val_rows ?? '—'}
              </StepValue>
            </Step>
          </Flow>

          {alignment && (
            <Callout>
              <CalloutTitle>
                <KnobHelp
                  label={t`When timestamps don’t line up`}
                  help={KNOB_HELP.alignment}
                />
              </CalloutTitle>
              <StatGrid>
                <Stat>
                  <StatLabel>
                    <Trans>Target + all drivers (exact)</Trans>
                  </StatLabel>
                  <StatValue>
                    {alignment.target_and_all_drivers_exact ?? '—'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>
                    <Trans>+ after driver ffill(</Trans>
                    {alignment.driver_ffill_limit ?? '?'})
                  </StatLabel>
                  <StatValue>
                    {alignment.target_and_all_drivers_after_ffill ?? '—'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>
                    <Trans>Enough for kNN (≥</Trans>
                    {alignment.knn_min_drivers_required ?? '?'})
                  </StatLabel>
                  <StatValue>
                    {alignment.target_and_enough_drivers_for_knn ?? '—'}
                  </StatValue>
                </Stat>
              </StatGrid>
              {alignment.pairwise_exact_overlap_with_target && (
                <Meta>
                  <Trans>Exact overlap with target:</Trans>{' '}
                  {Object.entries(alignment.pairwise_exact_overlap_with_target)
                    .map(([tag, n]) => `${short(tag)}=${n}`)
                    .join(' · ') || '—'}
                </Meta>
              )}
              <Meta style={{ marginTop: '0.35rem' }}>
                {alignment.explanation}
              </Meta>
            </Callout>
          )}

          <Callout>
            <CalloutTitle>
              <KnobHelp
                label={t`Where does validation fit in?`}
                help={KNOB_HELP.validation}
              />
            </CalloutTitle>
            <Meta>
              {validationNote ||
                t`Timeline Val chunks (if you assign any) calibrate the residual band. If Val is empty — which is normal for the prod-like “tail 10%” preset — band scale still uses spread calibration blocks carved out of Train. Separately, each MLP uses sklearn early_stopping on an internal holdout of its fit rows. So “no Val chunks” does not mean the MLP trains with zero validation.`}
            </Meta>
            {split && (split.val ?? 0) === 0 && (
              <Meta style={{ marginTop: '0.35rem' }}>
                <Trans>
                  Right now Val = 0 rows → band calibration will use spread
                  blocks inside Train (prod behavior).
                </Trans>
              </Meta>
            )}
          </Callout>

          {tagStats && Object.keys(tagStats).length > 0 && (
            <TagTable>
              <thead>
                <tr>
                  <th>
                    <Trans>Tag</Trans>
                  </th>
                  <th>
                    <Trans>Observed</Trans>
                  </th>
                  <th>
                    <Trans>Missing %</Trans>
                  </th>
                  <th>
                    <Trans>Native spacing</Trans>
                  </th>
                  <th>
                    <Trans>Longest gap</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(tagStats).map(([tag, s]) => (
                  <tr key={tag}>
                    <td title={tag}>{short(tag)}</td>
                    <td>
                      {s.observed}/{s.rows}
                    </td>
                    <td>{(s.missing_pct * 100).toFixed(1)}%</td>
                    <td>{fmtSpacing(s.native_spacing_seconds)}</td>
                    <td>
                      {s.longest_gap_periods !== null &&
                      s.longest_gap_periods !== undefined
                        ? `${s.longest_gap_periods}×`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TagTable>
          )}
        </>
      )}
    </Strip>
  )
}
